/**
 * IDOR / authorization contract (E01-F02; architecture.md §4.4 "Авторизация и
 * IDOR"; ADR 2026-03-04). Shared between api and web.
 *
 * This file is the single, reusable definition of the security rules that every
 * domain feature (projects, ai, inbox, admin/users, …) must obey. It carries NO
 * runtime DB access and NO Fastify dependency — only the contract types and the
 * pure predicates that encode the legacy invariants verbatim, so a service in
 * any epic can import them without pulling in server internals.
 *
 * Why this exists (the legacy holes it closes — docs/source-n8n/reference):
 *   - `_common.md` get_1_project: "НЕТ проверки принадлежности проекта
 *     пользователю … потенциальный IDOR". → `assertProjectVisible` / the scope
 *     predicate below.
 *   - `_common.md` get_projects: RBAC was inlined into the SQL WHERE (owner_id /
 *     admin / teamlead+team_id). → `projectVisibilityRule` documents the exact
 *     3 rules; the Prisma `where` builder lives in the api service.
 *   - `Users.md` create/update/delete/save_oauth: "userId берётся из body без
 *     сверки с сессией — IDOR" + "можно сменить role через update_user
 *     (эскалация привилегий)". → `IDENTITY_BODY_FIELDS` + the strip helpers in
 *     the api service ignore these body fields entirely.
 *   - branch-3-ai / branch-7-inbox: `const userId = body.userId || 'system'`. →
 *     identity is taken ONLY from the validated session, never the body.
 *
 * The single invariant, stated once for the whole codebase:
 *   Identity (userId, role, team_id) is derived EXCLUSIVELY from the validated
 *   session (`request.user`). Any owner_id / user_id / role / userId in a request
 *   body is data, never authority — it can neither escalate a role nor reassign
 *   an owner.
 */
import { z } from 'zod';
import { APP_ROLES } from '../auth.js';
/**
 * The body field names that legacy callers used to (wrongly) carry identity.
 * No service may read identity from any of these — they are stripped before a
 * body is trusted. Verbatim list of the fields the old n8n nodes consulted:
 *   - `userId`   — branch-3-ai / branch-7-inbox (`body.userId || 'system'`),
 *                  every Users command (create/update/delete/disconnect/oauth).
 *   - `user_id`  — snake_case variant seen in raw payloads.
 *   - `owner_id` — projects update/create owner reassignment.
 *   - `role`     — Users.update_user privilege escalation (`role` from body).
 */
export const IDENTITY_BODY_FIELDS = ['userId', 'user_id', 'owner_id', 'role'];
/**
 * The trusted authorization principal a service operates on. Mirrors the subset
 * of `AuthIdentity` (`../auth.ts`) that authorization decisions depend on — id,
 * role, team. Built ONLY from the session row (see `auth/session.ts`).
 */
export const authPrincipalSchema = z.object({
    /** `users.id` — the ONLY trusted owner identifier (becomes `owner_id`). */
    id: z.string().min(1),
    /** `users.role` — drives `requireRole` and the project visibility scope. */
    role: z.string().min(1),
    /** `users.team_id` — used by the teamlead visibility rule (nullable). */
    team_id: z.string().nullable(),
});
/**
 * Resolve the visibility scope for a principal — pure, no DB. The api service
 * (`projectAccessScope.ts`) maps this to a Prisma `where`. Keeping the decision
 * here (shared) lets web reason about it too (e.g. show/hide the "all projects"
 * affordance) using the exact same 3 rules as the server.
 */
export function projectVisibilityScope(principal) {
    if (principal.role === 'admin')
        return { kind: 'all' };
    if (principal.role === 'teamlead') {
        return { kind: 'team', teamId: principal.team_id, selfId: principal.id };
    }
    return { kind: 'own', ownerId: principal.id };
}
/**
 * Does the given visibility scope grant a principal access to a single project
 * with the given `owner_id`? This is the predicate that closes the legacy
 * `get_1_project` IDOR (no ownership check at all in the source). The api
 * service resolves the team membership set and calls this; web can mirror it.
 *
 * @param scope            the principal's visibility scope.
 * @param projectOwnerId   `projects.owner_id` of the target project.
 * @param teamMemberIds    ids of users in the principal's team (only consulted
 *                         for the `team` scope; resolved by the api service).
 */
export function scopeAllowsProject(scope, projectOwnerId, teamMemberIds = []) {
    switch (scope.kind) {
        case 'all':
            return true;
        case 'own':
            return projectOwnerId !== null && projectOwnerId === scope.ownerId;
        case 'team':
            // teamlead always sees their own projects + any team member's project.
            if (projectOwnerId === null)
                return false;
            if (projectOwnerId === scope.selfId)
                return true;
            return teamMemberIds.includes(projectOwnerId);
    }
}
/** Is `role` one of the known application roles (`admin`/`teamlead`/`recruiter`)? */
export function isAppRole(role) {
    return APP_ROLES.includes(role);
}
/**
 * Does the principal hold the required role? Role is read from the principal
 * (session), NEVER from a request body — the single check behind every
 * `requireRole('admin')` guard and the admin-only command parity
 * (`auth.js updateUserInfo()` hid nav-users for non-admins; the server enforces
 * the same on the API).
 */
export function principalHasRole(principal, role) {
    return principal.role === role;
}
/**
 * Re-exported for convenience so a feature that already imports the IDOR
 * contract does not also need to reach into `../auth.ts` for the role list.
 */
export { APP_ROLES } from '../auth.js';
//# sourceMappingURL=idor.js.map